import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp, Phone, MapPin, Package, Calendar, User, DollarSign, Truck, Hash, Clock, Search, Eye, X, Filter, SortAsc, SortDesc, RefreshCw } from 'lucide-react';
import host from '../../assets/js/Host';

const OrdersTable = () => {
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortBy, setSortBy] = useState('orderNumber');
    const [sortDirection, setSortDirection] = useState('asc');
    const [showSortMenu, setShowSortMenu] = useState(false);
    const [selectedDelayedDate, setSelectedDelayedDate] = useState('all');
    const [showDateFilter, setShowDateFilter] = useState(false);
    const [ordersData, setOrdersData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // جلب البيانات من API
    useEffect(() => {
        fetchOrders();
    }, []);

    const fetchOrders = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await fetch(`${host}users/mtab3a-orders?status=%D9%85%D8%A4%D8%AC%D9%84`);

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();

            // تحويل البيانات إلى التنسيق المطلوب
            const transformedData = data.orders?.map(order => ({
                id: order.id,
                orderNumber: `ORD-${order.id}`,
                customerPhone: order.phone || order.phone2 || 'غير محدد',
                customerName: order.name || 'غير محدد',
                deliveryCompany: order.printerOrders?.[0]?.prints?.name || order.page_name || 'غير محدد',
                shipmentDate: order.printerOrders?.[0]?.prints?.createdAt ?
                    new Date(order.printerOrders[0].prints.createdAt).toLocaleDateString('en-GB') :
                    (order.add_at ? new Date(order.add_at).toLocaleDateString('en-GB') : 'غير محدد'),
                delayedDate: order.Delayed ? new Date(order.Delayed).toLocaleDateString('en-GB') : 'غير محدد',
                orderContent: order.ordersFormItems?.map(item =>
                    `${item.components?.name || 'منتج'} (${item.count || 1})`
                ).join(', ') || order.notes2 || 'غير محدد',
                orderPrice: order.price ? `${order.price.toLocaleString()} IQD` : '0 IQD',
                employeeName: order.user?.name || 'غير محدد',
                customerAddress: `${order.government || ''} - ${order.city || ''}`.trim().replace(/^-\s*|-\s*$/g, '') || 'غير محدد',
                status: order.calls || 'غير محدد',
                notes: order.notes || '',
                government: order.government || '',
                city: order.city || '',
                shippingPrice: order.shping_price || 0,
                cost: order.cost || 0,
                attemptHistory: order.matab3alogs?.map(log => ({
                    employeeName: log.user?.name || 'غير محدد',
                    status: log.status || 'غير محدد',
                    date: log.createdAt ? new Date(log.createdAt).toLocaleDateString('en-GB') + ' ' +
                        new Date(log.createdAt).toLocaleTimeString('en-GB', { hour12: false }) : 'غير محدد'
                })) || []
            })) || [];

            setOrdersData(transformedData);
        } catch (err) {
            console.error('Error fetching orders:', err);
            setError('فشل في جلب البيانات. تأكد من تشغيل الخادم.');
        } finally {
            setLoading(false);
        }
    };

    const handleSort = (field) => {
        if (sortBy === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(field);
            setSortDirection('asc');
        }
        setShowSortMenu(false);
    };

    const getSortLabel = () => {
        const labels = {
            orderNumber: 'رقم الطلب',
            deliveryCompany: 'شركة التوصيل',
            shipmentDate: 'تاريخ الترحيل',
            delayedDate: 'تاريخ التأجيل'
        };
        return labels[sortBy] || 'رقم الطلب';
    };

    // استخراج التواريخ الفريدة للفلترة
    const getUniqueDates = () => {
        const dates = ordersData
            .map(order => order.delayedDate)
            .filter(date => date && date !== 'غير محدد');

        const uniqueDates = [...new Set(dates)];

        // ترتيب التواريخ من الأقدم للأحدث
        return uniqueDates.sort((a, b) => {
            const dateA = new Date(a.split('/').reverse().join('-'));
            const dateB = new Date(b.split('/').reverse().join('-'));
            return dateA - dateB;
        });
    };

    // حساب عدد الطلبات لكل تاريخ
    const getDateCount = (date) => {
        return ordersData.filter(order => order.delayedDate === date).length;
    };

    // تطبيق البحث والترتيب والفلترة
    const processedOrders = (() => {
        // أولاً: الترشيح حسب البحث
        let filtered = ordersData.filter(order => {
            const matchesSearch = order.orderNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
                order.customerPhone.includes(searchTerm) ||
                order.customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                order.orderContent.toLowerCase().includes(searchTerm.toLowerCase());
            return matchesSearch;
        });

        // ثانياً: الفلترة حسب تاريخ التأجيل
        if (selectedDelayedDate !== 'all') {
            filtered = filtered.filter(order => order.delayedDate === selectedDelayedDate);
        }

        // ثالثاً: الترتيب
        return filtered.sort((a, b) => {
            let aValue, bValue;

            switch (sortBy) {
                case 'orderNumber':
                    aValue = a.orderNumber;
                    bValue = b.orderNumber;
                    break;
                case 'deliveryCompany':
                    aValue = a.deliveryCompany;
                    bValue = b.deliveryCompany;
                    break;
                case 'shipmentDate':
                    aValue = new Date(a.shipmentDate);
                    bValue = new Date(b.shipmentDate);
                    break;
                case 'delayedDate':
                    aValue = a.delayedDate !== 'غير محدد' ? new Date(a.delayedDate.split('/').reverse().join('-')) : new Date(0);
                    bValue = b.delayedDate !== 'غير محدد' ? new Date(b.delayedDate.split('/').reverse().join('-')) : new Date(0);
                    break;
                default:
                    aValue = a[sortBy];
                    bValue = b[sortBy];
            }

            if (sortDirection === 'asc') {
                return aValue > bValue ? 1 : -1;
            } else {
                return aValue < bValue ? 1 : -1;
            }
        });
    })();

    const SortIcon = ({ field }) => {
        if (sortBy !== field) return <ChevronDown style={{ width: '16px', height: '16px', color: '#9CA3AF' }} />;
        return sortDirection === 'asc' ?
            <ChevronUp style={{ width: '16px', height: '16px', color: '#2563EB' }} /> :
            <ChevronDown style={{ width: '16px', height: '16px', color: '#2563EB' }} />;
    };

    const getStatusColor = (status) => {
        if (status === 'تم التأكيد' || status === 'تأكيد الطلب' || status === 'تم الاتصال') {
            return { backgroundColor: '#DCFCE7', color: '#166534' };
        } else if (status === 'لم يجب') {
            return { backgroundColor: '#FEE2E2', color: '#991B1B' };
        } else if (status === 'مشغول' || status === 'تأجيل') {
            return { backgroundColor: '#FEF3C7', color: '#92400E' };
        } else {
            return { backgroundColor: '#F3F4F6', color: '#374151' };
        }
    };

    const getAttemptsColor = (attempts) => {
        if (attempts <= 2) {
            return { backgroundColor: '#DCFCE7', color: '#166534' };
        } else if (attempts <= 4) {
            return { backgroundColor: '#FEF3C7', color: '#92400E' };
        } else {
            return { backgroundColor: '#FEE2E2', color: '#991B1B' };
        }
    };

    const OrderModal = ({ order, onClose }) => (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '16px'
        }}>
            <div style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                maxWidth: '1024px',
                width: '100%',
                maxHeight: '90vh',
                overflowY: 'auto',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
            }}>
                <div style={{
                    position: 'sticky',
                    top: 0,
                    backgroundColor: 'white',
                    borderBottom: '1px solid #E5E7EB',
                    padding: '24px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderRadius: '12px 12px 0 0',
                    direction: 'rtl'
                }}>
                    <h2 style={{ fontSize: '24px', fontWeight: 'bold', color: '#1F2937', margin: 0 }}>
                        تفاصيل الطلب {order.orderNumber}
                    </h2>
                    <button
                        onClick={onClose}
                        style={{
                            padding: '8px',
                            backgroundColor: 'transparent',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#F3F4F6'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                    >
                        <X style={{ width: '24px', height: '24px' }} />
                    </button>
                </div>

                <div style={{ padding: '24px', direction: 'rtl' }}>
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                        gap: '24px',
                        marginBottom: '32px'
                    }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                            {[
                                { icon: Hash, label: 'رقم الطلب', value: order.orderNumber, color: '#3B82F6' },
                                { icon: User, label: 'اسم العميل', value: order.customerName, color: '#10B981' },
                                { icon: Phone, label: 'هاتف العميل', value: order.customerPhone, color: '#10B981' },
                                { icon: Truck, label: 'شركة التوصيل', value: order.deliveryCompany, color: '#8B5CF6' },
                                { icon: Calendar, label: 'تاريخ الترحيل', value: order.shipmentDate, color: '#F59E0B' }
                            ].map((item, index) => (
                                <div key={index} style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '12px',
                                    padding: '16px',
                                    backgroundColor: `${item.color}10`,
                                    borderRadius: '8px'
                                }}>
                                    <item.icon style={{ width: '20px', height: '20px', color: item.color }} />
                                    <div>
                                        <p style={{ fontSize: '14px', color: '#6B7280', margin: '0 0 4px 0' }}>{item.label}</p>
                                        <p style={{ fontWeight: '600', color: '#1F2937', margin: 0 }}>{item.value}</p>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                            {[
                                { icon: Clock, label: 'تاريخ التأجيل', value: order.delayedDate, color: '#EF4444' },
                                { icon: Package, label: 'مادة الطلب', value: order.orderContent, color: '#14B8A6' },
                                { icon: DollarSign, label: 'سعر الطلب', value: order.orderPrice, color: '#F59E0B' },
                                { icon: User, label: 'موظف مثبت الطلب', value: order.employeeName, color: '#6366F1' }
                            ].map((item, index) => (
                                <div key={index} style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '12px',
                                    padding: '16px',
                                    backgroundColor: `${item.color}10`,
                                    borderRadius: '8px'
                                }}>
                                    <item.icon style={{ width: '20px', height: '20px', color: item.color }} />
                                    <div>
                                        <p style={{ fontSize: '14px', color: '#6B7280', margin: '0 0 4px 0' }}>{item.label}</p>
                                        <p style={{ fontWeight: '600', color: '#1F2937', margin: 0 }}>{item.value}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div style={{ marginBottom: '24px' }}>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            padding: '16px',
                            backgroundColor: '#F9FAFB',
                            borderRadius: '8px',
                            marginBottom: '12px'
                        }}>
                            <MapPin style={{ width: '20px', height: '20px', color: '#6B7280' }} />
                            <div>
                                <p style={{ fontSize: '14px', color: '#6B7280', margin: '0 0 4px 0' }}>عنوان العميل</p>
                                <p style={{ fontWeight: '600', color: '#1F2937', margin: 0 }}>{order.customerAddress}</p>
                            </div>
                        </div>

                        {order.notes && (
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px',
                                padding: '16px',
                                backgroundColor: '#FEF3C7',
                                borderRadius: '8px'
                            }}>
                                <Package style={{ width: '20px', height: '20px', color: '#F59E0B' }} />
                                <div>
                                    <p style={{ fontSize: '14px', color: '#92400E', margin: '0 0 4px 0' }}>ملاحظات</p>
                                    <p style={{ fontWeight: '600', color: '#92400E', margin: 0 }}>{order.notes}</p>
                                </div>
                            </div>
                        )}
                    </div>

                    <div>
                        <h3 style={{
                            fontSize: '20px',
                            fontWeight: 'bold',
                            color: '#1F2937',
                            marginBottom: '16px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}>
                            <Clock style={{ width: '20px', height: '20px' }} />
                            سجل المحاولات
                        </h3>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            {order.attemptHistory.map((attempt, index) => (
                                <div key={index} style={{
                                    border: '1px solid #E5E7EB',
                                    borderRadius: '8px',
                                    padding: '16px',
                                    transition: 'box-shadow 0.3s ease'
                                }}
                                    onMouseEnter={(e) => e.target.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1)'}
                                    onMouseLeave={(e) => e.target.style.boxShadow = 'none'}
                                >
                                    <div style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'flex-start',
                                        marginBottom: '8px'
                                    }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <User style={{ width: '16px', height: '16px', color: '#6B7280' }} />
                                            <span style={{ fontWeight: '600', color: '#1F2937' }}>{attempt.employeeName}</span>
                                        </div>
                                        <span style={{ fontSize: '14px', color: '#6B7280' }}>{attempt.date}</span>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <div style={{
                                            padding: '4px 12px',
                                            borderRadius: '16px',
                                            fontSize: '14px',
                                            fontWeight: '500',
                                            ...getStatusColor(attempt.status)
                                        }}>
                                            {attempt.status}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <div
            style={{
                minHeight: '100vh',
                background: 'linear-gradient(135deg, #EBF4FF 0%, #E0E7FF 100%)',
                padding: '16px',
                direction: 'rtl',
                fontFamily: 'Arial, sans-serif'
            }}
            onClick={(e) => {
                if (!e.target.closest('.sort-dropdown') && !e.target.closest('.date-filter')) {
                    setShowSortMenu(false);
                    setShowDateFilter(false);
                }
            }}
        >
            <style>
                {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
            </style>
            <div style={{ maxWidth: '95vw', margin: '0 auto', width: '100%' }}>
                <div style={{
                    backgroundColor: 'white',
                    borderRadius: '16px',
                    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                    overflow: 'hidden'
                }}>
                    {/* Header */}
                    <div style={{
                        background: 'linear-gradient(135deg, #2563EB 0%, #4F46E5 100%)',
                        padding: '24px'
                    }}>
                        <h1 style={{
                            fontSize: '32px',
                            fontWeight: 'bold',
                            color: 'white',
                            marginBottom: '16px',
                            margin: '0 0 16px 0',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '16px'
                        }}>
                            إدارة الطلبات
                            <button
                                onClick={fetchOrders}
                                disabled={loading}
                                style={{
                                    padding: '8px',
                                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                    border: '1px solid rgba(255, 255, 255, 0.3)',
                                    borderRadius: '8px',
                                    cursor: loading ? 'not-allowed' : 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    transition: 'all 0.3s ease'
                                }}
                                onMouseEnter={(e) => {
                                    if (!loading) {
                                        e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.3)';
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.2)';
                                }}
                            >
                                <RefreshCw style={{
                                    width: '20px',
                                    height: '20px',
                                    color: 'white',
                                    animation: loading ? 'spin 1s linear infinite' : 'none'
                                }} />
                            </button>
                        </h1>

                        {/* Search and Filter Bar */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
                                {/* Search Input */}
                                <div style={{ flex: '1', minWidth: '300px', position: 'relative' }}>
                                    <Search style={{
                                        position: 'absolute',
                                        right: '16px',
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        color: '#9CA3AF',
                                        width: '20px',
                                        height: '20px'
                                    }} />
                                    <input
                                        type="text"
                                        placeholder="البحث برقم الطلب، الهاتف، الاسم، أو المنتج..."
                                        style={{
                                            width: '100%',
                                            paddingRight: '48px',
                                            paddingLeft: '16px',
                                            paddingTop: '14px',
                                            paddingBottom: '14px',
                                            borderRadius: '12px',
                                            border: '2px solid rgba(255, 255, 255, 0.3)',
                                            fontSize: '16px',
                                            outline: 'none',
                                            boxSizing: 'border-box',
                                            textAlign: 'right',
                                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                            backdropFilter: 'blur(10px)',
                                            transition: 'all 0.3s ease'
                                        }}
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        onFocus={(e) => {
                                            e.target.style.borderColor = '#ffffff';
                                            e.target.style.backgroundColor = '#ffffff';
                                            e.target.style.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.15)';
                                        }}
                                        onBlur={(e) => {
                                            e.target.style.borderColor = 'rgba(255, 255, 255, 0.3)';
                                            e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.9)';
                                            e.target.style.boxShadow = 'none';
                                        }}
                                    />
                                </div>

                                {/* Date Filter Dropdown */}
                                <div style={{ position: 'relative' }} className="date-filter">
                                    <button
                                        onClick={() => setShowDateFilter(!showDateFilter)}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px',
                                            padding: '14px 20px',
                                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                            border: '2px solid rgba(255, 255, 255, 0.3)',
                                            borderRadius: '12px',
                                            cursor: 'pointer',
                                            fontSize: '14px',
                                            fontWeight: '500',
                                            color: '#374151',
                                            backdropFilter: 'blur(10px)',
                                            transition: 'all 0.3s ease',
                                            minWidth: '180px',
                                            justifyContent: 'space-between'
                                        }}
                                        onMouseEnter={(e) => {
                                            e.target.style.backgroundColor = '#ffffff';
                                            e.target.style.borderColor = '#ffffff';
                                            e.target.style.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.15)';
                                        }}
                                        onMouseLeave={(e) => {
                                            if (!showDateFilter) {
                                                e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.9)';
                                                e.target.style.borderColor = 'rgba(255, 255, 255, 0.3)';
                                                e.target.style.boxShadow = 'none';
                                            }
                                        }}
                                    >
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <Calendar style={{ width: '16px', height: '16px' }} />
                                            <span>
                                                {selectedDelayedDate === 'all' ? 'جميع التواريخ' : selectedDelayedDate}
                                            </span>
                                        </div>
                                        <ChevronDown style={{
                                            width: '16px',
                                            height: '16px',
                                            transform: showDateFilter ? 'rotate(180deg)' : 'rotate(0deg)',
                                            transition: 'transform 0.3s ease'
                                        }} />
                                    </button>

                                    {/* Date Filter Menu */}
                                    {showDateFilter && (
                                        <div style={{
                                            position: 'absolute',
                                            top: '100%',
                                            right: '0',
                                            marginTop: '8px',
                                            backgroundColor: 'white',
                                            border: '1px solid #E5E7EB',
                                            borderRadius: '12px',
                                            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                                            zIndex: 1000,
                                            minWidth: '220px',
                                            overflow: 'hidden',
                                            maxHeight: '300px',
                                            overflowY: 'auto'
                                        }}>
                                            {/* All Dates Option */}
                                            <button
                                                onClick={() => {
                                                    setSelectedDelayedDate('all');
                                                    setShowDateFilter(false);
                                                }}
                                                style={{
                                                    width: '100%',
                                                    padding: '12px 16px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '12px',
                                                    border: 'none',
                                                    backgroundColor: selectedDelayedDate === 'all' ? '#F3F4F6' : 'transparent',
                                                    cursor: 'pointer',
                                                    fontSize: '14px',
                                                    color: '#374151',
                                                    textAlign: 'right',
                                                    transition: 'background-color 0.2s ease'
                                                }}
                                                onMouseEnter={(e) => {
                                                    if (selectedDelayedDate !== 'all') {
                                                        e.target.style.backgroundColor = '#F9FAFB';
                                                    }
                                                }}
                                                onMouseLeave={(e) => {
                                                    if (selectedDelayedDate !== 'all') {
                                                        e.target.style.backgroundColor = 'transparent';
                                                    }
                                                }}
                                            >
                                                <Calendar style={{
                                                    width: '16px',
                                                    height: '16px',
                                                    color: selectedDelayedDate === 'all' ? '#2563EB' : '#6B7280'
                                                }} />
                                                <span>جميع التواريخ ({ordersData.length} طلب)</span>
                                            </button>

                                            {/* Individual Date Options */}
                                            {getUniqueDates().map((date) => (
                                                <button
                                                    key={date}
                                                    onClick={() => {
                                                        setSelectedDelayedDate(date);
                                                        setShowDateFilter(false);
                                                    }}
                                                    style={{
                                                        width: '100%',
                                                        padding: '12px 16px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                        border: 'none',
                                                        backgroundColor: selectedDelayedDate === date ? '#F3F4F6' : 'transparent',
                                                        cursor: 'pointer',
                                                        fontSize: '14px',
                                                        color: '#374151',
                                                        textAlign: 'right',
                                                        transition: 'background-color 0.2s ease'
                                                    }}
                                                    onMouseEnter={(e) => {
                                                        if (selectedDelayedDate !== date) {
                                                            e.target.style.backgroundColor = '#F9FAFB';
                                                        }
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        if (selectedDelayedDate !== date) {
                                                            e.target.style.backgroundColor = 'transparent';
                                                        }
                                                    }}
                                                >
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                        <Clock style={{
                                                            width: '16px',
                                                            height: '16px',
                                                            color: selectedDelayedDate === date ? '#2563EB' : '#EF4444'
                                                        }} />
                                                        <span>{date}</span>
                                                    </div>
                                                    <span style={{
                                                        fontSize: '12px',
                                                        color: '#6B7280',
                                                        backgroundColor: '#F3F4F6',
                                                        padding: '2px 8px',
                                                        borderRadius: '12px'
                                                    }}>
                                                        {getDateCount(date)}
                                                    </span>
                                                </button>
                                            ))}

                                            {getUniqueDates().length === 0 && (
                                                <div style={{
                                                    padding: '16px',
                                                    textAlign: 'center',
                                                    color: '#6B7280',
                                                    fontSize: '14px'
                                                }}>
                                                    لا توجد تواريخ تأجيل
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>

                                {/* Sort Dropdown */}
                                <div style={{ position: 'relative' }} className="sort-dropdown">
                                    <button
                                        onClick={() => setShowSortMenu(!showSortMenu)}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px',
                                            padding: '14px 20px',
                                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                            border: '2px solid rgba(255, 255, 255, 0.3)',
                                            borderRadius: '12px',
                                            cursor: 'pointer',
                                            fontSize: '14px',
                                            fontWeight: '500',
                                            color: '#374151',
                                            backdropFilter: 'blur(10px)',
                                            transition: 'all 0.3s ease',
                                            minWidth: '160px',
                                            justifyContent: 'space-between'
                                        }}
                                        onMouseEnter={(e) => {
                                            e.target.style.backgroundColor = '#ffffff';
                                            e.target.style.borderColor = '#ffffff';
                                            e.target.style.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.15)';
                                        }}
                                        onMouseLeave={(e) => {
                                            if (!showSortMenu) {
                                                e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.9)';
                                                e.target.style.borderColor = 'rgba(255, 255, 255, 0.3)';
                                                e.target.style.boxShadow = 'none';
                                            }
                                        }}
                                    >
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            {sortDirection === 'asc' ?
                                                <SortAsc style={{ width: '16px', height: '16px' }} /> :
                                                <SortDesc style={{ width: '16px', height: '16px' }} />
                                            }
                                            <span>ترتيب: {getSortLabel()}</span>
                                        </div>
                                        <ChevronDown style={{
                                            width: '16px',
                                            height: '16px',
                                            transform: showSortMenu ? 'rotate(180deg)' : 'rotate(0deg)',
                                            transition: 'transform 0.3s ease'
                                        }} />
                                    </button>

                                    {/* Sort Menu */}
                                    {showSortMenu && (
                                        <div style={{
                                            position: 'absolute',
                                            top: '100%',
                                            right: '0',
                                            marginTop: '8px',
                                            backgroundColor: 'white',
                                            border: '1px solid #E5E7EB',
                                            borderRadius: '12px',
                                            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                                            zIndex: 1000,
                                            minWidth: '200px',
                                            overflow: 'hidden'
                                        }}>
                                            {[
                                                { key: 'orderNumber', label: 'رقم الطلب', icon: Hash },
                                                { key: 'deliveryCompany', label: 'شركة التوصيل', icon: Truck },
                                                { key: 'shipmentDate', label: 'تاريخ الترحيل', icon: Calendar },
                                                { key: 'delayedDate', label: 'تاريخ التأجيل', icon: Clock }
                                            ].map((option) => (
                                                <button
                                                    key={option.key}
                                                    onClick={() => handleSort(option.key)}
                                                    style={{
                                                        width: '100%',
                                                        padding: '12px 16px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '12px',
                                                        border: 'none',
                                                        backgroundColor: sortBy === option.key ? '#F3F4F6' : 'transparent',
                                                        cursor: 'pointer',
                                                        fontSize: '14px',
                                                        color: '#374151',
                                                        textAlign: 'right',
                                                        transition: 'background-color 0.2s ease'
                                                    }}
                                                    onMouseEnter={(e) => {
                                                        if (sortBy !== option.key) {
                                                            e.target.style.backgroundColor = '#F9FAFB';
                                                        }
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        if (sortBy !== option.key) {
                                                            e.target.style.backgroundColor = 'transparent';
                                                        }
                                                    }}
                                                >
                                                    <option.icon style={{
                                                        width: '16px',
                                                        height: '16px',
                                                        color: sortBy === option.key ? '#2563EB' : '#6B7280'
                                                    }} />
                                                    <span>{option.label}</span>
                                                    {sortBy === option.key && (
                                                        <div style={{ marginRight: 'auto' }}>
                                                            {sortDirection === 'asc' ?
                                                                <SortAsc style={{ width: '14px', height: '14px', color: '#2563EB' }} /> :
                                                                <SortDesc style={{ width: '14px', height: '14px', color: '#2563EB' }} />
                                                            }
                                                        </div>
                                                    )}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Loading State */}
                    {loading && (
                        <div style={{
                            textAlign: 'center',
                            padding: '48px 0',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center'
                        }}>
                            <RefreshCw style={{
                                width: '48px',
                                height: '48px',
                                color: '#2563EB',
                                marginBottom: '16px',
                                animation: 'spin 1s linear infinite'
                            }} />
                            <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#2563EB', marginBottom: '8px' }}>
                                جاري تحميل البيانات...
                            </h3>
                        </div>
                    )}

                    {/* Error State */}
                    {error && (
                        <div style={{
                            textAlign: 'center',
                            padding: '48px 0',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center'
                        }}>
                            <X style={{ width: '48px', height: '48px', color: '#EF4444', marginBottom: '16px' }} />
                            <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#EF4444', marginBottom: '8px' }}>
                                خطأ في التحميل
                            </h3>
                            <p style={{ color: '#6B7280', marginBottom: '16px', margin: '0 0 16px 0' }}>{error}</p>
                            <button
                                onClick={fetchOrders}
                                style={{
                                    padding: '8px 16px',
                                    backgroundColor: '#2563EB',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    fontSize: '14px'
                                }}
                            >
                                <RefreshCw style={{ width: '16px', height: '16px' }} />
                                إعادة المحاولة
                            </button>
                        </div>
                    )}

                    {/* Table */}
                    {!loading && !error && (
                        <div style={{ overflowX: 'auto', width: '100%' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '1200px' }}>
                                <thead style={{ backgroundColor: '#F9FAFB', borderBottom: '1px solid #E5E7EB' }}>
                                    <tr>
                                        {[
                                            { key: 'orderNumber', label: 'رقم الطلب', sortable: true },
                                            { key: 'customerPhone', label: 'هاتف العميل', sortable: false },
                                            { key: 'deliveryCompany', label: 'شركة التوصيل', sortable: false },
                                            { key: 'shipmentDate', label: 'تاريخ الترحيل', sortable: false },
                                            { key: 'delayedDate', label: 'تاريخ التأجيل', sortable: false },
                                            { key: 'orderContent', label: 'مادة الطلب', sortable: false },
                                            { key: 'orderPrice', label: 'السعر', sortable: false },
                                            { key: 'employeeName', label: 'الموظف المثبت', sortable: false },
                                            { key: 'actions', label: 'الإجراءات', sortable: false }
                                        ].map((column) => (
                                            <th key={column.key} style={{
                                                padding: '16px 12px',
                                                textAlign: 'center',
                                                fontSize: '14px',
                                                fontWeight: '600',
                                                color: '#374151',
                                                minWidth: column.key === 'orderContent' ? '200px' :
                                                    column.key === 'customerPhone' ? '140px' :
                                                        column.key === 'deliveryCompany' ? '120px' :
                                                            column.key === 'employeeName' ? '150px' :
                                                                column.key === 'customerAddress' ? '200px' : 'auto'
                                            }}>
                                                {column.sortable ? (
                                                    <button
                                                        onClick={() => handleSort(column.key)}
                                                        style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: '8px',
                                                            background: 'none',
                                                            border: 'none',
                                                            cursor: 'pointer',
                                                            color: '#374151',
                                                            fontSize: '14px',
                                                            fontWeight: '600',
                                                            transition: 'color 0.2s ease'
                                                        }}
                                                        onMouseEnter={(e) => e.target.style.color = '#2563EB'}
                                                        onMouseLeave={(e) => e.target.style.color = '#374151'}
                                                    >
                                                        {column.label}
                                                        <SortIcon field={column.key} />
                                                    </button>
                                                ) : (
                                                    column.label
                                                )}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {processedOrders.map((order) => (
                                        <tr key={order.id} style={{
                                            borderBottom: '1px solid #E5E7EB',
                                            transition: 'background-color 0.2s ease'
                                        }}
                                            onMouseEnter={(e) => e.target.parentElement.style.backgroundColor = '#F9FAFB'}
                                            onMouseLeave={(e) => e.target.parentElement.style.backgroundColor = 'transparent'}
                                        >
                                            <td style={{ padding: '16px 12px', textAlign: 'center' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}>
                                                    <Hash style={{ width: '16px', height: '16px', color: '#2563EB' }} />
                                                    <span style={{ fontWeight: '600', color: '#111827', fontSize: '14px' }}>{order.orderNumber}</span>
                                                </div>
                                            </td>
                                            <td style={{ padding: '16px 12px', textAlign: 'center' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}>
                                                    <Phone style={{ width: '16px', height: '16px', color: '#10B981' }} />
                                                    <span style={{ color: '#374151', fontSize: '14px' }}>{order.customerPhone}</span>
                                                </div>
                                            </td>
                                            <td style={{ padding: '16px 12px', textAlign: 'center' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}>
                                                    <Truck style={{ width: '16px', height: '16px', color: '#8B5CF6' }} />
                                                    <span style={{ color: '#374151', fontSize: '14px' }}>{order.deliveryCompany}</span>
                                                </div>
                                            </td>
                                            <td style={{ padding: '16px 12px', textAlign: 'center' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}>
                                                    <Calendar style={{ width: '16px', height: '16px', color: '#F59E0B' }} />
                                                    <span style={{ color: '#374151', fontSize: '14px' }}>{order.shipmentDate}</span>
                                                </div>
                                            </td>
                                            <td style={{ padding: '16px 12px', textAlign: 'center' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}>
                                                    <Clock style={{ width: '16px', height: '16px', color: '#EF4444' }} />
                                                    <span style={{
                                                        color: order.delayedDate !== 'غير محدد' ? '#EF4444' : '#6B7280',
                                                        fontSize: '14px',
                                                        fontWeight: order.delayedDate !== 'غير محدد' ? '600' : '400'
                                                    }}>
                                                        {order.delayedDate}
                                                    </span>
                                                </div>
                                            </td>
                                            <td style={{ padding: '16px 12px', textAlign: 'center' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}>
                                                    <Package style={{ width: '16px', height: '16px', color: '#14B8A6' }} />
                                                    <span style={{
                                                        color: '#374151',
                                                        fontSize: '14px',
                                                        maxWidth: '250px',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap'
                                                    }}>
                                                        {order.orderContent}
                                                    </span>
                                                </div>
                                            </td>
                                            <td style={{ padding: '16px 12px', textAlign: 'center' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}>
                                                    <DollarSign style={{ width: '16px', height: '16px', color: '#F59E0B' }} />
                                                    <span style={{ fontWeight: '600', color: '#111827', fontSize: '14px' }}>{order.orderPrice}</span>
                                                </div>
                                            </td>
                                            <td style={{ padding: '16px 12px', textAlign: 'center' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}>
                                                    <User style={{ width: '16px', height: '16px', color: '#6366F1' }} />
                                                    <span style={{ color: '#374151', fontSize: '14px' }}>{order.employeeName}</span>
                                                </div>
                                            </td>
                                            <td style={{ padding: '16px 12px', textAlign: 'center' }}>
                                                <button
                                                    onClick={() => setSelectedOrder(order)}
                                                    style={{
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        gap: '8px',
                                                        padding: '8px 16px',
                                                        backgroundColor: '#2563EB',
                                                        color: 'white',
                                                        borderRadius: '8px',
                                                        border: 'none',
                                                        cursor: 'pointer',
                                                        fontSize: '14px',
                                                        fontWeight: '500',
                                                        transition: 'background-color 0.2s ease'
                                                    }}
                                                    onMouseEnter={(e) => e.target.style.backgroundColor = '#1D4ED8'}
                                                    onMouseLeave={(e) => e.target.style.backgroundColor = '#2563EB'}
                                                >
                                                    <Eye style={{ width: '16px', height: '16px' }} />
                                                    عرض التفاصيل
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                    )}

                    {/* Empty State */}
                    {!loading && !error && processedOrders.length === 0 && (
                        <div style={{
                            textAlign: 'center',
                            padding: '48px 0',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center'
                        }}>
                            <Package style={{ width: '64px', height: '64px', color: '#9CA3AF', marginBottom: '16px' }} />
                            <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#6B7280', marginBottom: '8px' }}>
                                لا توجد طلبات
                            </h3>
                            <p style={{ color: '#9CA3AF', margin: 0 }}>لم يتم العثور على طلبات تطابق البحث</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Modal */}
            {selectedOrder && (
                <OrderModal
                    order={selectedOrder}
                    onClose={() => setSelectedOrder(null)}
                />
            )}
        </div>
    );
};

export default OrdersTable;